import React, { useEffect, useState } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { 
  LayoutDashboard, Package, ShoppingCart, Users, Tag, Settings, LogOut,
  TrendingUp, DollarSign, ShoppingBag, UserCheck, ArrowUpRight, ArrowDownRight,
  BarChart3, Activity, Clock, CheckCircle, Truck, Menu, X, Bell, Search,
  Sun, Moon, ChevronDown, MoreHorizontal, Eye, Calendar, Filter, RefreshCw,
  Home, Layers, FileText, MessageSquare, HelpCircle, ChevronRight
} from 'lucide-react';
import { useAdminTheme } from '../contexts/AdminThemeContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator
} from '../components/ui/dropdown-menu';
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer
} from 'recharts';
import axios from 'axios';
import { AdminTour } from '../components/AdminTour';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = BACKEND_URL ? `${BACKEND_URL.replace(/\/$/, '')}/api` : '/api';
const TOUR_STATE_KEY = 'adminTourState';

const statusConfig = {
  pending: { label: 'Pendente', color: 'bg-amber-100 text-amber-700', dot: 'bg-amber-500' },
  confirmed: { label: 'Confirmado', color: 'bg-blue-100 text-blue-700', dot: 'bg-blue-500' },
  shipped: { label: 'Enviado', color: 'bg-purple-100 text-purple-700', dot: 'bg-purple-500' },
  delivered: { label: 'Entregue', color: 'bg-emerald-100 text-emerald-700', dot: 'bg-emerald-500' },
  cancelled: { label: 'Cancelado', color: 'bg-red-100 text-red-700', dot: 'bg-red-500' }
};

export const AdminDashboard = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { darkMode, toggleTheme, theme } = useAdminTheme();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [userRole, setUserRole] = useState(localStorage.getItem('userRole') || 'admin');
  const [userName, setUserName] = useState('');
  const [notifications, setNotifications] = useState([]);
  const [globalSearch, setGlobalSearch] = useState('');
  const [selectedYear, setSelectedYear] = useState(2026);
  const [tourOpen, setTourOpen] = useState(() => {
    try {
      const raw = localStorage.getItem(TOUR_STATE_KEY);
      const parsed = raw ? JSON.parse(raw) : null;
      return !!parsed?.open;
    } catch {
      return false;
    }
  });
  const [tourIndex, setTourIndex] = useState(() => {
    try {
      const raw = localStorage.getItem(TOUR_STATE_KEY);
      const parsed = raw ? JSON.parse(raw) : null;
      return Number.isFinite(parsed?.index) ? parsed.index : 0;
    } catch {
      return 0;
    }
  });

  useEffect(() => {
    try {
      localStorage.setItem(TOUR_STATE_KEY, JSON.stringify({ open: tourOpen, index: tourIndex }));
    } catch {
      // ignore
    }
  }, [tourOpen, tourIndex]);

  const runGlobalSearch = () => {
    const q = globalSearch.trim();
    if (!q) return;

    const params = new URLSearchParams();
    params.set('q', q);

    const looksLikeOrder = /^#?[a-f0-9\-]{6,}$/i.test(q);
    if (q.includes('@') || looksLikeOrder) {
      navigate(`/admin/orders?${params.toString()}`);
      return;
    }
    navigate(`/admin/products?${params.toString()}`);
  };

  const tourSteps = [
    {
      path: '/admin',
      selector: '[data-tour="admin-sidebar"]',
      title: '1. Menu Lateral',
      description: 'Use este menu para navegar entre as seções: Dashboard, Produtos, Pedidos, Clientes, Cupons e Categorias. Vamos conhecer cada uma!'
    },
    {
      path: '/admin',
      selector: '[data-tour="admin-stats"]',
      title: '2. Cards de Estatísticas',
      description: 'Visão rápida do negócio: Faturamento Total, Total de Pedidos, Clientes cadastrados e Produtos no catálogo.'
    },
    {
      path: '/admin',
      selector: '[data-tour="admin-charts"]',
      title: '3. Gráficos',
      description: 'Gráfico de faturamento dos últimos 6 meses, status dos pedidos e produtos mais vendidos.'
    },
    {
      path: '/admin',
      selector: '[data-tour="admin-theme"]',
      title: '4. Tema',
      description: 'Alterne entre modo claro e escuro. A preferência é salva automaticamente.'
    },
    {
      path: '/admin',
      selector: '[data-tour="admin-notifications"]',
      title: '5. Notificações',
      description: 'Alertas de pedidos pendentes e estoque baixo. O número vermelho mostra quantas não lidas.'
    },
    {
      path: '/admin/orders',
      selector: '[data-tour="orders-filters"]',
      title: '🛒 Pedidos - Filtros',
      description: 'Busque pedidos por ID ou email do cliente. Use o filtro de status para ver apenas Pendentes, Confirmados, Enviados, etc.'
    },
    {
      path: '/admin/orders',
      selector: '[data-tour="orders-table"]',
      title: '🛒 Pedidos - Tabela',
      description: 'Lista de pedidos com: ID, Cliente, Data, Total, Pagamento e Status. Clique no Status para mudar (Pendente → Confirmado → Enviado → Entregue). O Pagamento atualiza automaticamente!'
    },
    {
      path: '/admin/products',
      selector: '[data-tour="products-header"]',
      title: '📦 Produtos - Ações',
      description: 'Busque produtos pelo nome. Clique em "Novo Produto" para adicionar. Defina nome, preço, descrição, imagens, estoque e categoria.'
    },
    {
      path: '/admin/users',
      selector: '[data-tour="users-stats"]',
      title: '👥 Clientes - Estatísticas',
      description: 'Veja o total de usuários dividido por tipo: Programadores, Administradores e Clientes.'
    },
    {
      path: '/admin/users',
      selector: '[data-tour="users-table"]',
      title: '👥 Clientes - Tabela',
      description: 'Lista de usuários com nome, email, cargo e pedidos. Altere o cargo clicando no dropdown. Exclua usuários pelo botão de lixeira.'
    },
    {
      path: '/admin/coupons',
      selector: '[data-tour="coupons-header"]',
      title: '🏷️ Cupons - Criar',
      description: 'Clique em "Novo Cupom" para criar. Defina: código, tipo (% ou R$), valor, desconto máximo, compra mínima, limite de usos e expiração.'
    },
    {
      path: '/admin/categories',
      selector: '[data-tour="categories-header"]',
      title: '📁 Categorias - Gerenciar',
      description: 'Veja o total de categorias. Clique em "Nova Categoria" para criar. Defina nome, slug (URL), descrição e imagem.'
    },
    {
      path: '/admin',
      selector: '[data-tour="admin-user-menu"]',
      title: '✅ Tutorial Concluído!',
      description: 'Você conheceu todas as abas! Os dados atualizam automaticamente. Clique em Ajuda para rever este tutorial a qualquer momento.'
    }
  ];

  useEffect(() => {
    fetchStats();
    checkUserRole();

    const interval = setInterval(() => {
      fetchStats();
    }, 15000);

    return () => clearInterval(interval);
  }, [selectedYear]);

  useEffect(() => {
    const token = localStorage.getItem('adminToken');
    if (!token) return;

    const formatDateTime = (iso) => {
      if (!iso) return 'Agora';
      const date = new Date(iso);
      if (Number.isNaN(date.getTime())) return 'Agora';
      return date.toLocaleString('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    };

    const fetchNotifications = async () => {
      try {
        const response = await axios.get(`${API}/admin/stats`, {
          headers: { Authorization: `Bearer ${token}` },
          withCredentials: true
        });

        const pending = response.data?.orders_by_status?.pending || 0;
        const latestPendingAt = response.data?.latest_pending_order_created_at;
        const lowStockProducts = response.data?.low_stock_products || [];
        const lowStockThreshold = response.data?.low_stock_threshold;

        const next = [];

        if (pending > 0) {
          next.push({
            id: 'pending_orders',
            title: pending === 1 ? '1 pedido pendente' : `${pending} pedidos pendentes`,
            time: formatDateTime(latestPendingAt),
            read: false,
          });
        }

        if (lowStockProducts.length > 0) {
          const thresholdText = typeof lowStockThreshold === 'number' ? ` (<= ${lowStockThreshold})` : '';
          next.push({
            id: 'low_stock',
            title: lowStockProducts.length === 1
              ? `1 produto com estoque baixo${thresholdText}`
              : `${lowStockProducts.length} produtos com estoque baixo${thresholdText}`,
            time: 'Agora',
            read: false,
          });
        }

        setNotifications(next);
      } catch (error) {
        if (error.response?.status === 401 || error.response?.status === 403) {
          navigate('/admin/login');
        }
      }
    };

    fetchNotifications();
    const interval = setInterval(fetchNotifications, 15000);
    return () => clearInterval(interval);
  }, [navigate]);

  const checkUserRole = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      const response = await axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: true
      });
      setUserRole(response.data.role);
      setUserName(response.data.name || response.data.email);
      localStorage.setItem('userRole', response.data.role);
    } catch (error) {
      navigate('/admin/login');
    }
  };

  const isProgrammer = userRole === 'programmer';
  const canManageCatalog = userRole === 'programmer' || userRole === 'admin';

  const fetchStats = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      const response = await axios.get(`${API}/admin/stats?year=${selectedYear}`, {
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: true
      });
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
      if (error.response?.status === 401 || error.response?.status === 403) {
        navigate('/admin/login');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('adminToken');
    localStorage.removeItem('userRole');
    navigate('/admin/login');
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  const unreadNotifications = notifications.filter(n => !n.read).length;

  const menuItems = [
    { icon: LayoutDashboard, label: 'Dashboard', path: '/admin', show: true },
    { icon: Package, label: 'Produtos', path: '/admin/products', show: canManageCatalog },
    { icon: ShoppingCart, label: 'Pedidos', path: '/admin/orders', show: true },
    { icon: Users, label: 'Clientes', path: '/admin/users', show: true },
    { icon: Tag, label: 'Cupons', path: '/admin/coupons', show: true },
    { icon: Layers, label: 'Categorias', path: '/admin/categories', show: canManageCatalog },
  ];

  if (loading) {
    return (
      <div className={`min-h-screen ${theme.bg} flex items-center justify-center`}>
        <div className="flex flex-col items-center space-y-4">
          <div className="w-12 h-12 border-4 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
          <p className={theme.textMuted}>Carregando...</p>
        </div>
      </div>
    );
  }

  const revenueData = (Array.isArray(stats?.monthly_chart) && stats.monthly_chart.length > 0)
    ? stats.monthly_chart
    : [
      { month: 'Jan', revenue: 0 },
      { month: 'Fev', revenue: 0 },
      { month: 'Mar', revenue: 0 },
      { month: 'Abr', revenue: 0 },
      { month: 'Mai', revenue: 0 },
      { month: 'Jun', revenue: 0 },
      { month: 'Jul', revenue: 0 },
      { month: 'Ago', revenue: 0 },
      { month: 'Set', revenue: 0 },
      { month: 'Out', revenue: 0 },
      { month: 'Nov', revenue: 0 },
      { month: 'Dez', revenue: 0 },
    ];

  return (
    <div className={`min-h-screen ${theme.bg} flex`}>
      <aside data-tour="admin-sidebar" className={`${sidebarCollapsed ? 'w-20' : 'w-64'} ${theme.sidebar} border-r transition-all duration-300 flex flex-col fixed h-full z-20`}>
        <div className={`h-16 flex items-center ${sidebarCollapsed ? 'justify-center px-2' : 'px-6'} border-b ${darkMode ? 'border-slate-700' : 'border-gray-200'}`}>
          <div className="flex items-center space-x-3">
            <img
              src="/logo-pcs.png"
              alt="PCS Dutos"
              className={`h-12 w-auto ${darkMode ? '' : 'brightness-0'}`}
            />
            {!sidebarCollapsed && (
              <div>
                <span className={`font-bold text-lg ${theme.text}`}>PCS Admin</span>
                <span className={`block text-xs ${theme.textMuted}`}>Painel de Controle</span>
              </div>
            )}
          </div>
        </div>

        <nav className="flex-1 py-6 px-3 space-y-1 overflow-y-auto">
          {menuItems.filter(item => item.show).map((item) => {
            const isActive = location.pathname === item.path;
            return (
              <Link
                key={item.path}
                to={item.path}
                className={`flex items-center ${sidebarCollapsed ? 'justify-center' : ''} space-x-3 px-4 py-3 rounded-xl transition-all duration-200 ${
                  isActive 
                    ? theme.active + ' border'
                    : `${theme.textMuted} ${theme.hover}`
                }`}
              >
                <item.icon className="h-5 w-5 flex-shrink-0" />
                {!sidebarCollapsed && <span className="font-medium">{item.label}</span>}
              </Link>
            );
          })}
        </nav>

        <div className={`p-4 border-t ${darkMode ? 'border-slate-700' : 'border-gray-200'}`}>
          <button
            onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
            className={`w-full flex items-center ${sidebarCollapsed ? 'justify-center' : ''} space-x-3 px-4 py-3 rounded-xl ${theme.textMuted} ${theme.hover} transition-colors`}
          >
            <ChevronRight className={`h-5 w-5 transition-transform ${sidebarCollapsed ? '' : 'rotate-180'}`} />
            {!sidebarCollapsed && <span>Recolher</span>}
          </button>
        </div>
      </aside>

      <main className={`flex-1 ${sidebarCollapsed ? 'ml-20' : 'ml-64'} transition-all duration-300`}>
        <header className={`${theme.header} backdrop-blur-xl border-b px-6 py-4 sticky top-0 z-10`}>
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <div>
                <h1 className={`text-2xl font-bold ${theme.text}`}>Dashboard</h1>
                <p className={`text-sm ${theme.textMuted}`}>Bem-vindo de volta, {userName || 'Admin'}!</p>
              </div>
            </div>

            <div className="flex items-center space-x-3">
              <div className="hidden md:flex items-center">
                <div className="relative">
                  <Search className={`absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 ${theme.textMuted}`} />
                  <Input 
                    placeholder="Buscar..." 
                    value={globalSearch}
                    onChange={(e) => setGlobalSearch(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        runGlobalSearch();
                      }
                    }}
                    className={`pl-10 w-64 ${theme.input} rounded-xl border focus:ring-2 focus:ring-blue-500`}
                  />
                </div>
              </div>

              <Button
                variant="ghost"
                size="icon"
                onClick={toggleTheme}
                className={`rounded-xl ${theme.hover}`}
                data-tour="admin-theme"
              >
                {darkMode ? <Sun className="h-5 w-5 text-yellow-500" /> : <Moon className="h-5 w-5 text-slate-600" />}
              </Button>

              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="icon" className={`rounded-xl ${theme.hover} relative`} data-tour="admin-notifications">
                    <Bell className={`h-5 w-5 ${theme.textMuted}`} />
                    {unreadNotifications > 0 && (
                      <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center">
                        {unreadNotifications}
                      </span>
                    )}
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className={`w-80 ${darkMode ? 'bg-slate-800 border-slate-700' : 'bg-white border-gray-200'}`}>
                  <div className={`px-4 py-3 border-b ${darkMode ? 'border-slate-700' : 'border-gray-200'}`}>
                    <p className={`font-semibold ${theme.text}`}>Notificações</p>
                    <p className={`text-sm ${theme.textMuted}`}>{unreadNotifications} não lidas</p>
                  </div>
                  {notifications.map((notif) => (
                    <DropdownMenuItem key={notif.id} className={`px-4 py-3 ${!notif.read ? (darkMode ? 'bg-slate-700/50' : 'bg-blue-50') : ''}`}>
                      <div className="flex items-start space-x-3">
                        <div className={`w-2 h-2 rounded-full mt-2 ${notif.read ? 'bg-gray-300' : 'bg-blue-500'}`}></div>
                        <div>
                          <p className={`text-sm font-medium ${theme.text}`}>{notif.title}</p>
                          <p className={`text-xs ${theme.textMuted}`}>{notif.time}</p>
                        </div>
                      </div>
                    </DropdownMenuItem>
                  ))}
                  <DropdownMenuSeparator className={darkMode ? 'bg-slate-700' : 'bg-gray-200'} />
                  <DropdownMenuItem
                    className="justify-center text-blue-500 font-medium"
                    onClick={() => navigate('/admin/notifications')}
                  >
                    Ver todas
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>

              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" className={`flex items-center space-x-3 px-3 py-2 rounded-xl ${theme.hover}`} data-tour="admin-user-menu">
                    <Avatar className="h-9 w-9">
                      <AvatarFallback className="bg-gradient-to-br from-blue-500 to-purple-600 text-white">
                        {userName?.charAt(0)?.toUpperCase() || 'A'}
                      </AvatarFallback>
                    </Avatar>
                    <div className="hidden md:block text-left">
                      <p className={`text-sm font-medium ${theme.text}`}>{userName || 'Admin'}</p>
                      <p className={`text-xs ${theme.textMuted}`}>{userRole === 'programmer' ? 'Programador' : 'Administrador'}</p>
                    </div>
                    <ChevronDown className={`h-4 w-4 ${theme.textMuted}`} />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className={`w-56 ${darkMode ? 'bg-slate-800 border-slate-700' : 'bg-white border-gray-200'}`}>
                  <DropdownMenuItem onClick={() => navigate('/')} className={theme.text}>
                    <Home className="h-4 w-4 mr-2" />
                    Ver Site
                  </DropdownMenuItem>
                  <DropdownMenuItem className={theme.text} onClick={() => navigate('/admin/settings')}>
                    <Settings className="h-4 w-4 mr-2" />
                    Configurações
                  </DropdownMenuItem>
                  <DropdownMenuItem className={theme.text} onClick={() => { setTourIndex(0); setTourOpen(true); }}>
                    <HelpCircle className="h-4 w-4 mr-2" /> Ajuda
                  </DropdownMenuItem>
                  <DropdownMenuSeparator className={darkMode ? 'bg-slate-700' : 'bg-gray-200'} />
                  <DropdownMenuItem onClick={handleLogout} className="text-red-500">
                    <LogOut className="h-4 w-4 mr-2" />
                    Sair
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </header>

        <div className="p-6 space-y-6">
          <div data-tour="admin-stats" className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <Card className={`${theme.card} border rounded-2xl overflow-hidden`}>
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className={`text-sm font-medium ${theme.textMuted}`}>Faturamento Total ({selectedYear})</p>
                    <p className={`text-2xl font-bold ${theme.text} mt-1`}>{formatCurrency((typeof stats?.year_revenue === 'number' ? stats.year_revenue : 0))}</p>
                    <div className="flex items-center mt-2 text-emerald-500 text-sm">
                      <ArrowUpRight className="h-4 w-4 mr-1" />
                      <span>+12.5%</span>
                      <span className={`${theme.textMuted} ml-1`}>vs mês anterior</span>
                    </div>
                  </div>
                  <div className="w-14 h-14 bg-blue-100 dark:bg-blue-900/30 rounded-2xl flex items-center justify-center">
                    <DollarSign className="h-7 w-7 text-blue-600" />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className={`${theme.card} border rounded-2xl overflow-hidden`}>
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className={`text-sm font-medium ${theme.textMuted}`}>Total de Pedidos</p>
                    <p className={`text-2xl font-bold ${theme.text} mt-1`}>{stats?.total_orders || 0}</p>
                    <div className="flex items-center mt-2 text-emerald-500 text-sm">
                      <ArrowUpRight className="h-4 w-4 mr-1" />
                      <span>+8.2%</span>
                      <span className={`${theme.textMuted} ml-1`}>vs mês anterior</span>
                    </div>
                  </div>
                  <div className="w-14 h-14 bg-emerald-100 dark:bg-emerald-900/30 rounded-2xl flex items-center justify-center">
                    <ShoppingBag className="h-7 w-7 text-emerald-600" />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className={`${theme.card} border rounded-2xl overflow-hidden`}>
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className={`text-sm font-medium ${theme.textMuted}`}>Clientes</p>
                    <p className={`text-2xl font-bold ${theme.text} mt-1`}>{stats?.total_users || 0}</p>
                    <div className="flex items-center mt-2 text-emerald-500 text-sm">
                      <ArrowUpRight className="h-4 w-4 mr-1" />
                      <span>+5 novos</span>
                      <span className={`${theme.textMuted} ml-1`}>esta semana</span>
                    </div>
                  </div>
                  <div className="w-14 h-14 bg-purple-100 dark:bg-purple-900/30 rounded-2xl flex items-center justify-center">
                    <Users className="h-7 w-7 text-purple-600" />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className={`${theme.card} border rounded-2xl overflow-hidden`}>
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className={`text-sm font-medium ${theme.textMuted}`}>Produtos</p>
                    <p className={`text-2xl font-bold ${theme.text} mt-1`}>{stats?.total_products || 0}</p>
                    <div className="flex items-center mt-2 text-amber-500 text-sm">
                      <Package className="h-4 w-4 mr-1" />
                      <span>{stats?.total_categories || 0} categorias</span>
                    </div>
                  </div>
                  <div className="w-14 h-14 bg-amber-100 dark:bg-amber-900/30 rounded-2xl flex items-center justify-center">
                    <Package className="h-7 w-7 text-amber-600" />
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          <div data-tour="admin-charts" className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <Card className={`lg:col-span-2 ${theme.card} border rounded-2xl`}>
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className={theme.text}>Faturamento</CardTitle>
                    <CardDescription className={theme.textMuted}>Ano de {selectedYear}</CardDescription>
                  </div>
                  <div className="flex items-center space-x-2">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="outline" size="sm" className={`${theme.input} rounded-lg`}>
                          <Calendar className="h-4 w-4 mr-2" />
                          {selectedYear}
                          <ChevronDown className="h-4 w-4 ml-2" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className={`${darkMode ? 'bg-slate-800 border-slate-700' : 'bg-white border-gray-200'}`}>
                        {[2026, 2027, 2028, 2029].map((year) => (
                          <DropdownMenuItem
                            key={year}
                            onClick={() => setSelectedYear(year)}
                            className={`${theme.text} ${selectedYear === year ? 'bg-blue-100 dark:bg-blue-900/30' : ''}`}
                          >
                            {year}
                          </DropdownMenuItem>
                        ))}
                      </DropdownMenuContent>
                    </DropdownMenu>
                    <Button variant="ghost" size="icon" className={`rounded-lg ${theme.hover}`} onClick={fetchStats}>
                      <RefreshCw className={`h-4 w-4 ${theme.textMuted}`} />
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="h-80">
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={revenueData}>
                      <defs>
                        <linearGradient id="colorRevenueNew" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.2}/>
                          <stop offset="95%" stopColor="#3b82f6" stopOpacity={0}/>
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke={darkMode ? '#334155' : '#e5e7eb'} />
                      <XAxis dataKey="month" stroke={darkMode ? '#94a3b8' : '#6b7280'} />
                      <YAxis stroke={darkMode ? '#94a3b8' : '#6b7280'} tickFormatter={(value) => `R$${value/1000}k`} />
                      <Tooltip 
                        contentStyle={{ 
                          backgroundColor: darkMode ? '#1e293b' : '#fff', 
                          border: darkMode ? '1px solid #334155' : '1px solid #e5e7eb', 
                          borderRadius: '12px',
                          boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
                        }}
                        labelStyle={{ color: darkMode ? '#fff' : '#111' }}
                        formatter={(value) => [formatCurrency(value), 'Faturamento']}
                      />
                      <Area type="monotone" dataKey="revenue" stroke="#3b82f6" strokeWidth={3} fillOpacity={1} fill="url(#colorRevenueNew)" />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>

            <Card className={`${theme.card} border rounded-2xl`}>
              <CardHeader>
                <CardTitle className={theme.text}>Status dos Pedidos</CardTitle>
                <CardDescription className={theme.textMuted}>Distribuição atual</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {[
                    { label: 'Pendentes', value: stats?.orders_by_status?.pending || 0, color: 'bg-amber-500', bgColor: 'bg-amber-100' },
                    { label: 'Confirmados', value: stats?.orders_by_status?.confirmed || 0, color: 'bg-blue-500', bgColor: 'bg-blue-100' },
                    { label: 'Enviados', value: stats?.orders_by_status?.shipped || 0, color: 'bg-purple-500', bgColor: 'bg-purple-100' },
                    { label: 'Entregues', value: stats?.orders_by_status?.delivered || 0, color: 'bg-emerald-500', bgColor: 'bg-emerald-100' },
                  ].map((status, i) => {
                    const total = (stats?.orders_by_status?.pending || 0) + (stats?.orders_by_status?.confirmed || 0) + 
                                  (stats?.orders_by_status?.shipped || 0) + (stats?.orders_by_status?.delivered || 0);
                    const percentage = total > 0 ? (status.value / total) * 100 : 0;
                    return (
                      <div key={i} className="space-y-2">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center space-x-2">
                            <div className={`w-3 h-3 rounded-full ${status.color}`}></div>
                            <span className={`text-sm font-medium ${theme.text}`}>{status.label}</span>
                          </div>
                          <span className={`text-sm font-semibold ${theme.text}`}>{status.value}</span>
                        </div>
                        <div className={`h-2 rounded-full ${darkMode ? 'bg-slate-700' : 'bg-gray-100'}`}>
                          <div className={`h-2 rounded-full ${status.color}`} style={{ width: `${percentage}%` }}></div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card className={`${theme.card} border rounded-2xl`}>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className={theme.text}>Pedidos Recentes</CardTitle>
                    <CardDescription className={theme.textMuted}>Últimas transações</CardDescription>
                  </div>
                  <Button variant="ghost" size="sm" onClick={() => navigate('/admin/orders')} className="text-blue-500">
                    Ver todos <ChevronRight className="h-4 w-4 ml-1" />
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {(stats?.recent_orders || []).slice(0, 5).map((order, index) => (
                    <div key={index} className={`flex items-center justify-between p-4 rounded-xl ${darkMode ? 'bg-slate-700/50' : 'bg-gray-50'}`}>
                      <div className="flex items-center space-x-4">
                        <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${darkMode ? 'bg-slate-600' : 'bg-white shadow-sm'}`}>
                          <ShoppingBag className={`h-5 w-5 ${theme.textMuted}`} />
                        </div>
                        <div>
                          <p className={`font-medium ${theme.text}`}>#{order.id.slice(0, 8)}</p>
                          <p className={`text-sm ${theme.textMuted}`}>{formatDate(order.created_at)}</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className={`font-semibold ${theme.text}`}>{formatCurrency(order.total)}</p>
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${statusConfig[order.status]?.color}`}>
                          <span className={`w-1.5 h-1.5 rounded-full ${statusConfig[order.status]?.dot} mr-1.5`}></span>
                          {statusConfig[order.status]?.label}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            <Card className={`${theme.card} border rounded-2xl`}>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className={theme.text}>Produtos Mais Vendidos</CardTitle>
                    <CardDescription className={theme.textMuted}>Top 5 produtos</CardDescription>
                  </div>
                  {isProgrammer && (
                    <Button variant="ghost" size="sm" onClick={() => navigate('/admin/products')} className="text-blue-500">
                      Gerenciar <ChevronRight className="h-4 w-4 ml-1" />
                    </Button>
                  )}
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {(stats?.top_products || []).map((product, index) => (
                    <div key={index} className={`flex items-center justify-between p-4 rounded-xl ${darkMode ? 'bg-slate-700/50' : 'bg-gray-50'}`}>
                      <div className="flex items-center space-x-4">
                        <div className={`w-10 h-10 rounded-xl flex items-center justify-center font-bold text-white ${
                          index === 0 ? 'bg-gradient-to-br from-amber-400 to-amber-600' :
                          index === 1 ? 'bg-gradient-to-br from-gray-400 to-gray-600' :
                          index === 2 ? 'bg-gradient-to-br from-amber-600 to-amber-800' :
                          'bg-gradient-to-br from-blue-500 to-blue-700'
                        }`}>
                          {index + 1}
                        </div>
                        <div>
                          <p className={`font-medium ${theme.text}`}>{product.name}</p>
                          <p className={`text-sm ${theme.textMuted}`}>{product.sold} vendidos</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="font-semibold text-emerald-500">{formatCurrency(product.revenue)}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </main>

      <AdminTour
        open={tourOpen}
        onClose={() => setTourOpen(false)}
        steps={tourSteps}
        storageKey="adminTourCompleted"
        initialIndex={tourIndex}
        onIndexChange={setTourIndex}
      />
    </div>
  );
};
